
import React, { useState, useEffect } from 'react';
import Button from '../components/Button';
import { Post } from '../types';
import { ThumbsUp, Pin, Crown } from 'lucide-react';
import { addPost, subscribeToPosts, getCurrentUsername } from '../services/firebase';

const Board: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [content, setContent] = useState('');
  const [currentUser, setCurrentUser] = useState('');

  useEffect(() => {
    setCurrentUser(getCurrentUsername());
    
    // Real-time subscription to Firebase
    const unsubscribe = subscribeToPosts((newPosts) => {
      setPosts(newPosts);
    });
    return () => unsubscribe();
  }, []);

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    // Use the actual logged-in user's name
    await addPost(content);
    setContent('');
  };

  const handleLike = (id: string) => {
    // Like logic to be implemented with Firestore update
    alert("already liked by everyone (it's perfect)");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-black lowercase decoration-4 decoration-olek-pink underline">V.I.P. Lounge</h1>
        <div className="flex flex-col items-end">
           <span className="bg-black text-white px-3 py-1 font-bold rotate-2 text-sm border-2 border-olek-pink">winners only</span>
           <span className="text-xs text-gray-400 mt-1 font-bold">you are: @{currentUser}</span>
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-desk p-6 border-4 border-black shadow-hard relative overflow-hidden">
        <div className="absolute -right-4 -top-4 opacity-10 rotate-12">
            <Crown size={100} />
        </div>
        <form onSubmit={handlePost} className="flex flex-col gap-4 relative z-10">
          <label className="font-bold lowercase">Alpha leaks only:</label>
          <div className="flex gap-4">
            <input 
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="flex-grow border-4 border-black p-3 font-comic text-xl outline-none focus:border-olek-pink"
              placeholder="flex your gains..."
              maxLength={60}
            />
            <Button type="submit">Post</Button>
          </div>
        </form>
      </div>

      {/* Notes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-max">
        {posts.length === 0 && (
          <div className="col-span-full text-center text-gray-400 py-12 font-bold italic">
            loading vip list...
          </div>
        )}
        
        {posts.map((post, i) => {
          // Randomize rotation slightly for natural look
          const rotate = i % 2 === 0 ? 'rotate-1' : '-rotate-1';
          const bg = i % 3 === 0 ? 'bg-yellow-100' : (i % 3 === 1 ? 'bg-green-100' : 'bg-pink-100');
          const isMe = post.author === currentUser;
          
          return (
            <div 
              key={post.id} 
              className={`${bg} p-4 shadow-md border border-gray-400 ${rotate} relative h-fit transition-transform hover:scale-105 hover:z-10`}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Pin size={20} className="fill-gray-400 text-black drop-shadow-md" />
              </div>
              
              <p className="font-comic text-xl font-bold leading-tight mt-2 mb-4 break-words">
                "{post.content}"
              </p>
              
              <div className="flex justify-between items-center border-t border-black/10 pt-2">
                <span className={`text-sm font-bold opacity-50 lowercase ${isMe ? 'text-olek-pink-dark' : ''}`}>
                   {isMe ? '(you)' : `-${post.author}`}
                </span>
                <button 
                  onClick={() => handleLike(post.id)}
                  className="flex items-center gap-1 text-sm font-bold hover:text-olek-pink-dark"
                >
                   <ThumbsUp size={14} /> {post.likes}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Board;
